import {
  buildObjectivesTree,
  addChildObjectivesToNode,
  findAllNodesById,
  buildHierarchicalChildrenMap,
  addNestedChildrenToNode,
  buildChildrenMapRecursively,
} from "src/components/GoalsExplore/utils/objectivesTree.util";
import { updateTreeObject } from "src/shared/TreeView/TreeView.util";

import { GOALS_ACTIONS } from "../actionTypes";

const goalsReducer = (state, action) => {
  switch (action.type) {
    case GOALS_ACTIONS.LOAD_ROOT_OBJECTIVES: {
      const objectives = action.payload.objectives || [];

      const rawObjectives = [...(objectives || [])];

      const childrenMap = {};

      objectives.forEach((obj) => {
        buildChildrenMapRecursively(objectives, childrenMap, obj.id);
      });

      const treeData = buildObjectivesTree(objectives, childrenMap);

      treeData.forEach((node) => {
        addNestedChildrenToNode(node, childrenMap);
      });

      return {
        ...state,
        treeData,
        rawObjectives,
        loading: false,
        error: null,
      };
    }

    case GOALS_ACTIONS.LOAD_BRANCH_OBJECTIVES: {
      const { objectives = [], parentNodeId } = action.payload;

      const rawObjectives = [...(objectives || [])];

      const parentNodes = findAllNodesById(state.treeData, parentNodeId);

      // If parent node doesn't exist in tree, add it as a new root node
      if (parentNodes.length === 0) {
        // Find the parent objective in the returned objectives
        const parentObjective = objectives.find(
          (obj) => obj.id === parentNodeId
        );

        if (!parentObjective) {
          // Parent not found in response, return unchanged
          return {
            ...state,
            rawObjectives,
          };
        }

        // Build childrenMap for this branch (parent + its descendants)
        const childrenMap = {};

        buildChildrenMapRecursively(
          objectives,
          childrenMap,
          parentObjective.id,
          true
        );

        // Build tree node for the parent objective
        const newTreeNode = buildObjectivesTree(
          [parentObjective],
          childrenMap
        )[0];

        // Add nested children
        addNestedChildrenToNode(newTreeNode, childrenMap);

        // Add to treeData as a new root node
        return {
          ...state,
          treeData: [...state.treeData, newTreeNode],
          rawObjectives,
          loading: false,
          error: null,
        };
      }

      // Parent node exists, add children to it
      const { directChildren, childrenMap } = buildHierarchicalChildrenMap(
        objectives,
        parentNodeId
      );

      parentNodes.forEach((parentNode) => {
        addChildObjectivesToNode(parentNode, directChildren, childrenMap);

        if (parentNode.children && Array.isArray(parentNode.children)) {
          parentNode.children.forEach((childNode) => {
            if (!childNode.isPlaceholder) {
              addNestedChildrenToNode(childNode, childrenMap);
            }
          });
        }
      });

      return {
        ...state,
        rawObjectives,
        treeData: updateTreeObject(state.treeData),
        loading: false,
        error: null,
      };
    }

    case GOALS_ACTIONS.UPDATE_OBJECTIVE_ATTRIBUTES: {
      const { objectiveId, attributes } = action.payload;

      const nodes = findAllNodesById(state.treeData, objectiveId);

      if (nodes.length === 0) {
        return state;
      }
      nodes.forEach((node) => {
        if (!node.objective) {
          return;
        }
        const existingAttributes = node.objective?.attributes || [];
        const merged = [...existingAttributes];
        attributes?.forEach((updated) => {
          if (updated.id && updated.attributeType) {
            const index = merged.findIndex(
              (a) =>
                a.attributeType === updated.attributeType && a.id === updated.id
            );
            if (index >= 0) {
              merged[index] = { ...merged[index], ...updated };
            } else {
              merged.push(updated);
            }
          }
        });
        // NOTE:: Object assign throws an error when updating the objective attributes
        // eslint-disable-next-line no-param-reassign
        node.objective = { ...node.objective, attributes: merged };
      });
      return {
        ...state,
        treeData: updateTreeObject(state.treeData),
      };
    }

    case GOALS_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case GOALS_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
};

export default goalsReducer;
